import { createTheme, Theme } from "@mui/material/styles";
import { StyleConfig } from "types";
import { mediaUrl } from "services/strapi.service";

const FONT_STACKS: Record<string, string> = {
  sansSerif: [
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Roboto",
    "Oxygen",
    "Ubuntu",
    "Cantarell",
    "Fira Sans",
    "Droid Sans",
    "Helvetica Neue",
    "sans-serif",
  ].join(","),
  serif: ["Georgia", "Cambria", "Times New Roman", "Times", "serif"].join(","),
  monospace: ["SFMono-Regular", "Menlo", "Monaco", "Consolas", "Courier New", "monospace"].join(","),
  inter: [
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Roboto",
    "Helvetica Neue",
    "sans-serif",
  ].join(","),
};

const DEFAULT_STYLE: {
  backgroundColor: string;
  panelColor: string;
  textColor: string;
  secondaryTextColor: string;
  buttonColor: string;
  buttonTextColor: string;
  borderColor: string;
  errorColor: string;
  cornerRoundness: number;
  bannerColor: string;
  linkColor: string;
} = {
  backgroundColor: "#00001c",
  panelColor: "#1a1a2e",
  textColor: "#ffffff",
  secondaryTextColor: "#b3b3c2",
  buttonColor: "#2563eb",
  buttonTextColor: "#ffffff",
  borderColor: "#3a3a4a",
  errorColor: "#f87171",
  cornerRoundness: 8,
  bannerColor: "#2563eb",
  linkColor: "#2563eb",
};

// Luminance-based dark/light inference: cheap and avoids requiring the
// backend to send an explicit `mode` field the schema doesn't have.
function isDarkColor(hex: string): boolean {
  const normalized = hex.replace("#", "");
  const expanded = normalized.length === 3 ? normalized.split("").map((c) => c + c).join("") : normalized;
  if (expanded.length !== 6) return true;
  const value = Number.parseInt(expanded, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

export function buildThemeFromStyleConfig(style?: StyleConfig | null): Theme {
  const backgroundColor = style?.backgroundColor || DEFAULT_STYLE.backgroundColor;
  const panelColor = style?.panelColor || DEFAULT_STYLE.panelColor;
  const textColor = style?.textColor || DEFAULT_STYLE.textColor;
  const secondaryTextColor = style?.secondaryTextColor || DEFAULT_STYLE.secondaryTextColor;
  const buttonColor = style?.buttonColor || DEFAULT_STYLE.buttonColor;
  const buttonTextColor = style?.buttonTextColor || DEFAULT_STYLE.buttonTextColor;
  const borderColor = style?.borderColor || DEFAULT_STYLE.borderColor;
  const errorColor = style?.errorColor || DEFAULT_STYLE.errorColor;
  const cornerRoundness = typeof style?.cornerRoundness === "number" ? style.cornerRoundness : DEFAULT_STYLE.cornerRoundness;
  const bannerColor = style?.bannerColor || DEFAULT_STYLE.bannerColor;
  const linkColor = style?.linkColor || DEFAULT_STYLE.linkColor;
  const fontFamily = (style?.font && FONT_STACKS[style.font]) || FONT_STACKS.sansSerif;

  return createTheme({
    palette: {
      mode: isDarkColor(backgroundColor) ? "dark" : "light",
      background: {
        default: backgroundColor,
        paper: panelColor,
      },
      text: {
        primary: textColor,
        secondary: secondaryTextColor,
      },
      primary: {
        main: buttonColor,
        contrastText: buttonTextColor,
      },
      secondary: {
        main: linkColor,
      },
      info: {
        main: bannerColor,
      },
      error: {
        main: errorColor,
      },
      divider: borderColor,
    },
    shape: {
      borderRadius: cornerRoundness,
    },
    typography: {
      fontFamily,
    },
  });
}

export function getLogoUrl(style?: StyleConfig | null): string | null {
  return mediaUrl(style?.logo);
}
